import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { safeLocalStorage } from '@/utils/storage';

export interface CartItem {
  serviceId: string;
  subServiceId: string;
  name: string;
  subServiceName: string;
  price: number;
  duration: string;
  imageUrl?: string;
  quantity: number;
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: Omit<CartItem, 'quantity'>) => void;
  removeItem: (subServiceId: string) => void;
  updateQuantity: (subServiceId: string, delta: number) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

interface StoredCart {
  items: CartItem[];
  timestamp: number;
}

const CART_STORAGE_KEY = 'fixeveryday_cart';
const CART_EXPIRY_MS = 24 * 60 * 60 * 1000;

// Maximum limits to prevent abuse
const MAX_NAME_LENGTH = 200;
const MAX_QUANTITY = 50;
const MAX_PRICE = 500_000; // ₹5L — no single service should exceed this

/**
 * Validates a single cart item from localStorage.
 * Prevents price manipulation, XSS injection via names, and impossible quantities.
 * This is a security boundary — never trust client-side storage.
 */
const isValidCartItem = (item: unknown): item is CartItem => {
  if (!item || typeof item !== 'object') return false;
  const obj = item as Record<string, unknown>;

  // Required string fields must exist and be reasonable length
  if (typeof obj.serviceId !== 'string' || obj.serviceId.length === 0 || obj.serviceId.length > 100) return false;
  if (typeof obj.subServiceId !== 'string' || obj.subServiceId.length === 0 || obj.subServiceId.length > 100) return false;
  if (typeof obj.name !== 'string' || obj.name.length === 0 || obj.name.length > MAX_NAME_LENGTH) return false;
  if (typeof obj.subServiceName !== 'string' || obj.subServiceName.length === 0 || obj.subServiceName.length > MAX_NAME_LENGTH) return false;
  if (typeof obj.duration !== 'string' || obj.duration.length > MAX_NAME_LENGTH) return false;

  // Price must be a positive finite number within business limits
  if (typeof obj.price !== 'number' || !Number.isFinite(obj.price) || obj.price <= 0 || obj.price > MAX_PRICE) return false;

  // Quantity must be a positive integer within business limits
  if (typeof obj.quantity !== 'number' || !Number.isInteger(obj.quantity) || obj.quantity <= 0 || obj.quantity > MAX_QUANTITY) return false;

  // Optional imageUrl — if present, must be a safe URL
  if (obj.imageUrl !== undefined && obj.imageUrl !== null) {
    if (typeof obj.imageUrl !== 'string' || obj.imageUrl.length > 2000) return false;
    // Block javascript: and data: protocol attacks
    const lowerUrl = obj.imageUrl.toLowerCase().trim();
    if (lowerUrl.startsWith('javascript:') || lowerUrl.startsWith('data:text')) return false;
  }

  return true;
};

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const saved = safeLocalStorage.getItem(CART_STORAGE_KEY);
      if (!saved) return [];

      const parsed: StoredCart | CartItem[] = JSON.parse(saved);

      // Handle legacy format (plain array)
      if (Array.isArray(parsed)) {
        return parsed.filter(isValidCartItem);
      }

      // Check expiry
      const now = Date.now();
      if (typeof parsed.timestamp !== 'number' || now - parsed.timestamp > CART_EXPIRY_MS) {
        safeLocalStorage.removeItem(CART_STORAGE_KEY);
        return [];
      }

      // Validate each item
      if (!Array.isArray(parsed.items)) return [];
      return parsed.items.filter(isValidCartItem);
    } catch (error) {
      console.error('Error parsing cart data from localStorage:', error);
      safeLocalStorage.removeItem(CART_STORAGE_KEY); // Clear corrupted data
      return [];
    }
  });

  useEffect(() => {
    const dataToSave: StoredCart = {
      items,
      timestamp: Date.now()
    };
    safeLocalStorage.setItem(CART_STORAGE_KEY, JSON.stringify(dataToSave));
  }, [items]);

  const addItem = (newItem: Omit<CartItem, 'quantity'>) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.subServiceId === newItem.subServiceId);
      if (existing) {
        return prev.map((i) =>
          i.subServiceId === newItem.subServiceId ? { ...i, quantity: Math.min(i.quantity + 1, MAX_QUANTITY) } : i
        );
      }
      return [...prev, { ...newItem, quantity: 1 }];
    });
  };

  const removeItem = (subServiceId: string) => {
    setItems((prev) => prev.filter((i) => i.subServiceId !== subServiceId));
  };

  const updateQuantity = (subServiceId: string, delta: number) => {
    setItems((prev) =>
      prev.map((i) => {
        if (i.subServiceId === subServiceId) {
          const newQty = Math.max(0, Math.min(i.quantity + delta, MAX_QUANTITY));
          return { ...i, quantity: newQty };
        }
        return i;
      }).filter((i) => i.quantity > 0)
    );
  };

  const clearCart = () => setItems([]);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQuantity, clearCart, totalItems, totalPrice }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
